import { Component, ChangeDetectionStrategy, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { SelectModule } from '@openng/optimus-ui/select';

import { RoleOption } from '../../../core/services/team-user.service';

interface EditFormData {
  email: string;
  fullName: string;
  position: string;
  systemRole: number;
}

/** "Edit Employee" dialog; email is read-only and role can be protected. */
@Component({
  selector: 'app-user-edit-modal',
  imports: [FormsModule, ButtonModule, DialogModule, InputTextModule, SelectModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      header="Edit Employee"
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [baseZIndex]="1000000"
      styleClass="w-full max-w-lg">
      <form (ngSubmit)="submitted.emit()" class="flex flex-col gap-4">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label for="emailEdit" class="font-semibold">Email</label>
            <input
              pInputText
              id="emailEdit"
              [(ngModel)]="formData().email"
              name="emailEdit"
              type="email"
              placeholder="Enter email address"
              [disabled]="true"
              class="w-full" />
          </div>

          <div class="flex flex-col gap-2">
            <label for="fullNameEdit" class="font-semibold">Full Name <span class="text-red-500">*</span></label>
            <input
              pInputText
              id="fullNameEdit"
              [(ngModel)]="formData().fullName"
              name="fullNameEdit"
              placeholder="Enter full name"
              [class.ng-invalid]="!!formErrors()['fullName']"
              [class.ng-dirty]="!!formErrors()['fullName']"
              class="w-full" />
            @if (formErrors()['fullName']) {
              <small class="p-error">{{ formErrors()['fullName'] }}</small>
            }
          </div>

          <div class="flex flex-col gap-2">
            <label for="positionEdit" class="font-semibold">Position</label>
            <input
              pInputText
              id="positionEdit"
              [(ngModel)]="formData().position"
              name="positionEdit"
              placeholder="Enter position"
              class="w-full" />
          </div>

          <div class="flex flex-col gap-2">
            <label for="systemRoleEdit" class="font-semibold">Role <span class="text-red-500">*</span></label>
            <p-select
              [options]="roleOptions()"
              [(ngModel)]="formData().systemRole"
              name="systemRoleEdit"
              placeholder="Select a role"
              optionLabel="label"
              optionValue="value"
              styleClass="w-full"
              [disabled]="!canEditRole()"
              [appendTo]="'body'">
            </p-select>
            @if (formErrors()['systemRole']) {
              <small class="p-error">{{ formErrors()['systemRole'] }}</small>
            }
          </div>
        </div>

        <div class="flex justify-end gap-2 pt-4">
          <p-button label="Cancel" severity="secondary" (onClick)="cancelled.emit()" type="button"></p-button>
          <p-button label="Update" [loading]="loading()" type="submit" icon="pi pi-check"></p-button>
        </div>
      </form>
    </p-dialog>
  `
})
export class UserEditModalComponent {
  readonly visible = model(false);
  readonly formData = input.required<EditFormData>();
  readonly roleOptions = input<RoleOption[]>([]);
  readonly formErrors = input<Record<string, string>>({});
  readonly canEditRole = input(true);
  readonly loading = input(false);

  readonly submitted = output<void>();
  readonly cancelled = output<void>();
}
